import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/JsonLd";

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

function Turnstile({ onVerify }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const renderedRef = useRef(false);

  const renderWidget = useCallback(() => {
    if (renderedRef.current || !containerRef.current || !window.turnstile) return;
    renderedRef.current = true;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token) => onVerify(token),
      "expired-callback": () => onVerify(""),
      theme: "light",
    });
  }, [onVerify]);

  useEffect(() => {
    if (window.turnstile) {
      renderWidget();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => renderWidget();
    document.head.appendChild(script);
    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        renderedRef.current = false;
      }
    };
  }, [renderWidget]);

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}

export default function ContactPage() {
  usePageMeta(
    "Cere Audit Gratuit - Ordell WebCare",
    "Cere un audit gratuit pentru site-ul tău WordPress. Îți spunem ce riscuri vedem, ce pachet ți se potrivește și ce pași recomandăm."
  );

  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    website: "", site_type: "", main_problem: "", help_type: "", message: "",
  });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Te rugăm completează cel puțin numele și emailul.");
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Te rugăm completează verificarea de securitate.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, cf_turnstile_response: turnstileToken }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Cererea ta a fost trimisă cu succes! Te vom contacta în curând.");
        setForm({ name: "", company: "", email: "", phone: "", website: "", site_type: "", main_problem: "", help_type: "", message: "" });
        setTurnstileToken("");
        if (window.turnstile) window.turnstile.reset();
      } else {
        toast.error(data.message || "A apărut o eroare.");
      }
    } catch {
      toast.error("A apărut o eroare. Te rugăm încearcă din nou.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <JsonLd page="contact" />
      {/* Hero */}
      <section data-testid="contact-hero" className="py-16 lg:py-24">
        <div className="section-container max-w-3xl">
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Cere audit gratuit pentru site-ul tău WordPress
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-4">
            Îți spunem clar ce riscuri vedem, ce pachet ți se potrivește și ce pași recomandăm pentru mai multă stabilitate și mai puțin stres.
          </p>
          <p className="text-sm text-[#4A5568] leading-relaxed">
            Nu trebuie să știi exact ce ai nevoie înainte să ne scrii. Spune-ne câteva lucruri despre site-ul tău și despre problemele pe care vrei să le eviți, iar noi revenim cu o recomandare clară.
          </p>
        </div>
      </section>

      {/* Form */}
      <section data-testid="contact-form-section" className="pb-20 lg:pb-28">
        <div className="section-container max-w-2xl">
          <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-[#1A1D20]">Nume *</Label>
                <Input
                  id="name" name="name" value={form.name} onChange={handleChange}
                  data-testid="contact-name-input"
                  className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                  placeholder="Numele tău"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-sm font-medium text-[#1A1D20]">Companie</Label>
                <Input
                  id="company" name="company" value={form.company} onChange={handleChange}
                  data-testid="contact-company-input"
                  className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                  placeholder="Numele companiei"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#1A1D20]">Email *</Label>
                <Input
                  id="email" name="email" type="email" value={form.email} onChange={handleChange}
                  data-testid="contact-email-input"
                  className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                  placeholder="email@companie.ro"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium text-[#1A1D20]">Telefon</Label>
                <Input
                  id="phone" name="phone" value={form.phone} onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                  placeholder="+40..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website" className="text-sm font-medium text-[#1A1D20]">Website</Label>
              <Input
                id="website" name="website" value={form.website} onChange={handleChange}
                data-testid="contact-website-input"
                className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1A1D20]">Tip site</Label>
              <Select value={form.site_type} onValueChange={(val) => setForm({ ...form, site_type: val })}>
                <SelectTrigger data-testid="contact-site-type-select" className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 rounded-xl">
                  <SelectValue placeholder="Selectează tipul site-ului" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prezentare">Site de prezentare</SelectItem>
                  <SelectItem value="business">Site business</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="altul">Altul</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="main_problem" className="text-sm font-medium text-[#1A1D20]">Care este principala problemă?</Label>
              <Input
                id="main_problem" name="main_problem" value={form.main_problem} onChange={handleChange}
                data-testid="contact-problem-input"
                className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                placeholder="Descrie pe scurt problema principală"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="help_type" className="text-sm font-medium text-[#1A1D20]">Ce tip de ajutor cauți?</Label>
              <Input
                id="help_type" name="help_type" value={form.help_type} onChange={handleChange}
                data-testid="contact-help-input"
                className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl"
                placeholder="Mentenanță, suport, audit, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium text-[#1A1D20]">Mesaj</Label>
              <Textarea
                id="message" name="message" value={form.message} onChange={handleChange}
                data-testid="contact-message-input"
                className="bg-white border-[#0F2942]/10 focus:ring-2 focus:ring-[#1A4A40]/30 focus:border-[#1A4A40] rounded-xl min-h-[120px]"
                placeholder="Spune-ne mai multe despre situația ta..."
              />
            </div>

            {TURNSTILE_SITE_KEY && (
              <div data-testid="turnstile-container">
                <Turnstile onVerify={setTurnstileToken} />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="relative z-10 bg-[#0F2942] text-white font-medium px-8 py-3.5 rounded-xl hover:bg-[#0F2942]/90 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? "Se trimite..." : "Trimite cererea"} <Send className="w-4 h-4" />
            </button>

            <p className="text-xs text-[#64748B] pb-8">
              Te contactăm cu o evaluare inițială și următorii pași recomandați.
            </p>
          </form>
        </div>
      </section>

      {/* Trust Block */}
      <section data-testid="contact-trust" className="py-16 lg:py-20 bg-[#F1F3F5]">
        <div className="section-container max-w-2xl">
          <h2 className="font-['Outfit'] text-xl sm:text-2xl tracking-tight font-semibold text-[#1A1D20] mb-4">
            Fără jargon inutil. Fără presiune. Fără promisiuni goale.
          </h2>
          <p className="text-sm text-[#4A5568] leading-relaxed mb-8">
            Auditul tehnic inițial are un scop simplu: să îți spunem clar ce vedem, unde există riscuri și ce am recomanda mai departe.
          </p>

          <div className="bg-white rounded-2xl p-6 border border-[#0F2942]/10">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A1D20] mb-4">Contactează-ne direct</h3>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <a href="tel:+40734311307" data-testid="contact-phone-link" className="flex items-center gap-2 text-sm text-[#0F2942] font-medium hover:text-[#0F2942]/80 transition-colors">
                <Phone className="w-4 h-4" />
                +40 (734) 311 307
              </a>
              <a href="https://wa.me/40734311307" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-2 text-sm text-[#1A4A40] font-medium hover:text-[#1A4A40]/80 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            </div>
            <p className="text-xs text-[#64748B]">
              Luni - Vineri, 11:00 - 19:00
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
